import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Card, CardContent } from "./ui/card";
import { useAuth } from "../contexts/AuthContext";
import { LoginRequest } from "../api/types";
import { authAPI } from "../api/axiosClient";
import TFAForm from "./TFAForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import AdminPasswordResetForm from "./AdminPasswordResetForm";
import { Eye, EyeOff } from "lucide-react";
import { IMaskInput } from "react-imask";
import { SimpleDropdown } from "./ui/simple-dropdown";
import { toast } from "react-hot-toast";
import { jwtDecode } from "jwt-decode";

// JWT Token Types
interface JWTPayload {
  sub: string;
  jti: string;
  iat: number;
  UserName: string;
  UserFullName: string;
  UserType: string | string[]; // Array olarak geliyor: ["SchoolManager", "Parent"]
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role":
    | string
    | string[];
  Permission?: string[];
  nbf: number;
  exp: number;
  iss: string;
  aud: string;
}

// User Type
interface User {
  id: string;
  username: string;
  fullName: string;
  email?: string;
  userType: string;
  role: string;
  permissions: string[];
}

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated } = useAuth();

  // JWT Token decode fonksiyonu
  const decodeJWT = (token: string): JWTPayload | null => {
    try {
      return jwtDecode<JWTPayload>(token);
    } catch (error) {
      console.error("JWT decode error:", error);
      return null;
    }
  };

  // JWT'den kullanıcı bilgilerini çıkarma
const getDefaultPermissionsByRole = (role: string): string[] => {
  if (role === "SchoolManager") {
    return [
      "users.view",
      "users.create",
      "users.edit",
      "users.delete",
      "students.view",
      "students.create",
      "students.edit",
      "students.delete",
      "finance.view",
      "finance.create",
      "finance.edit",
      "events.view",
      "events.create",
      "events.edit",
      "events.delete",
      "schools.view",
      "classes.view",
      "parents.view",
      "reports.view",
    ];
  }
  if (role === "SchoolUser") {
    return ["students.view", "events.view"];
  }
  return [];
};

const getUserFromToken = (token: string): User | null => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;

  const userTypes = Array.isArray(decoded.UserType) ? decoded.UserType : [decoded.UserType];
  const role = userTypes.includes("SchoolManager") ? "SchoolManager" : userTypes[0];

  const tokenPermissions = decoded.Permission ?? [];
  const permissions =
    tokenPermissions.length > 0 ? tokenPermissions : getDefaultPermissionsByRole(role);

  return {
    id: decoded.sub,
    username: decoded.UserName,
    fullName: decoded.UserFullName,
    userType: userTypes.join(", "),
    role,
    permissions,
  };
};


  const [formData, setFormData] = useState<LoginRequest>({
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showTFA, setShowTFA] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showAdminPasswordReset, setShowAdminPasswordReset] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminPasswordResetData, setAdminPasswordResetData] = useState<{
    userId: string;
    passwordResetToken: string;
  } | null>(null);

  const [countryCode, setCountryCode] = useState("90");
  const [selectedCountryCode, setSelectedCountryCode] = useState<{
    id: string | number;
    label: string;
    value: string | number;
  } | null>({ id: "90", label: "🇹🇷 +90", value: "90" });

  const countryCodes = [
    { code: "90", name: "Türkiye (+90)", flag: "🇹🇷" },
    { code: "1", name: "USA (+1)", flag: "🇺🇸" },
    { code: "44", name: "UK (+44)", flag: "🇬🇧" },
    { code: "49", name: "Germany (+49)", flag: "🇩🇪" },
    { code: "33", name: "France (+33)", flag: "🇫🇷" },
    { code: "39", name: "Italy (+39)", flag: "🇮🇹" },
    { code: "34", name: "Spain (+34)", flag: "🇪🇸" },
    { code: "31", name: "Netherlands (+31)", flag: "🇳🇱" },
    { code: "32", name: "Belgium (+32)", flag: "🇧🇪" },
    { code: "46", name: "Sweden (+46)", flag: "🇸🇪" },
    { code: "47", name: "Norway (+47)", flag: "🇳🇴" },
    { code: "45", name: "Denmark (+45)", flag: "🇩🇰" },
    { code: "358", name: "Finland (+358)", flag: "🇫🇮" },
    { code: "7", name: "Russia (+7)", flag: "🇷🇺" },
    { code: "86", name: "China (+86)", flag: "🇨🇳" },
    { code: "81", name: "Japan (+81)", flag: "🇯🇵" },
    { code: "82", name: "South Korea (+82)", flag: "🇰🇷" },
    { code: "91", name: "India (+91)", flag: "🇮🇳" },
    { code: "971", name: "UAE (+971)", flag: "🇦🇪" },
    { code: "966", name: "Saudi Arabia (+966)", flag: "🇸🇦" },
    { code: "20", name: "Egypt (+20)", flag: "🇪🇬" },
    { code: "27", name: "South Africa (+27)", flag: "🇿🇦" },
    { code: "55", name: "Brazil (+55)", flag: "🇧🇷" },
    { code: "54", name: "Argentina (+54)", flag: "🇦🇷" },
    { code: "52", name: "Mexico (+52)", flag: "🇲🇽" },
    { code: "1", name: "Canada (+1)", flag: "🇨🇦" },
    { code: "61", name: "Australia (+61)", flag: "🇦🇺" },
    { code: "64", name: "New Zealand (+64)", flag: "🇳🇿" },
  ];

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.username?.trim()) {
      newErrors.username = "Telefon numarası gereklidir";
    } else {
      const phoneNumber = formData.username.replace(/\D/g, ""); // Sadece rakamları al
      // Ülke koduna göre farklı validation kuralları
      if (countryCode === "90") {
        // Türkiye: 10 haneli (5xx xxx xx xx)
        if (phoneNumber.length !== 10) {
          newErrors.username = "Türkiye telefon numarası 10 haneli olmalıdır";
        } else if (!phoneNumber.startsWith("5")) {
          newErrors.username = "Türkiye telefon numarası 5 ile başlamalıdır";
        }
      } else if (countryCode === "1") {
        // USA/Canada: 10 haneli
        if (phoneNumber.length !== 10) {
          newErrors.username = "Telefon numarası 10 haneli olmalıdır";
        }
      } else if (countryCode === "44") {
        // UK: 10-11 haneli
        if (phoneNumber.length < 10 || phoneNumber.length > 11) {
          newErrors.username = "UK telefon numarası 10-11 haneli olmalıdır";
        }
      } else {
        // Diğer ülkeler için genel validation
        if (phoneNumber.length < 6 || phoneNumber.length > 15) {
          newErrors.username = "Geçerli bir telefon numarası girin";
        }
      }
    }

    if (!formData.password?.trim()) {
      newErrors.password = "Şifre gereklidir";
    } else if (formData.password.length < 6) {
      newErrors.password = "Şifre en az 6 karakter olmalıdır";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Telefon numarasını birleştir: 90 + 5425019511 = 905425019511
      const phoneNumber = formData.username?.replace(/\s/g, "") || "";

      // Admin login için yeni API'yi kullan
      const adminLoginData = {
        phoneCode: countryCode,
        phoneNumber: phoneNumber,
        password: formData.password || "",
      };

      console.log("Admin login attempt:", {
        countryCode,
        phoneNumber: formData.username,
        fullPhoneNumber: phoneNumber,
        password: formData.password ? "***" : "undefined",
      });

      const response = await authAPI.adminLogin(adminLoginData);

      if (response.success && response.data) {
        const {
          userId,
          token,
          refreshToken,
          refreshTokenExpiresAt,
          passwordResetToken,
        } = response.data;

        // Eğer passwordResetToken varsa, şifre değiştirme formuna yönlendir
        if (passwordResetToken) {
          console.log("Password reset required");
          setAdminPasswordResetData({
            userId,
            passwordResetToken,
          });
          setShowAdminPasswordReset(true);
          return;
        }

        // Eğer token varsa, normal login akışına devam et
        if (token) {
          // Token'ları localStorage'a kaydet
          localStorage.setItem("authToken", token);
          if (refreshToken) {
            localStorage.setItem("refreshToken", refreshToken);
          }
          if (refreshTokenExpiresAt) {
            localStorage.setItem(
              "refreshTokenExpiresAt",
              refreshTokenExpiresAt,
            );
          }
          localStorage.setItem("userId", userId);

          // Remember me
          if (rememberMe) {
            localStorage.setItem("rememberMe", "true");
          }

          // AuthContext'i güncelle
          const userFromToken = getUserFromToken(token);
          console.log("User from token:", userFromToken);
          if (userFromToken) {
            setUser(userFromToken);
            setIsAuthenticated(true);
            console.log("Admin login successful");
            navigate("/dashboard");
          } else {
            console.error("Failed to decode user from token");
          }
        } else {
          console.error("No token received");
        }
      } else {
        console.error("Admin login failed:", response.message);
        toast.error(response.message || "Giriş başarısız oldu");
      }
    } catch (err: any) {
      console.error("Admin login error:", err);
       const detail =
          err?.response?.data?.Detail ??
          err?.response?.data?.detail ??
          err?.response?.data?.message ??
          err?.message;

    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTFASuccess = (token: string) => {
    // TFA verification successful - token already stored in TFAForm
    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
    }
    navigate("/dashboard"); // Redirect to dashboard
    console.log("TFA verification successful, token stored:", token);
  };

  const handleTFABack = () => {
    setShowTFA(false);
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleForgotPasswordBack = () => {
    setShowForgotPassword(false);
  };

  const handleForgotPasswordSuccess = () => {
    setShowForgotPassword(false);
    // Show a brief success message or just go back to login
  };

  // Show TFA form if login requires it
  if (showTFA) {
    // Telefon numarasını birleştir: 90 + 5425019511 = 905425019511
    const fullPhoneNumber = `${countryCode}${formData.username?.replace(/\s/g, "") || ""}`;

    return (
      <TFAForm
        onSuccess={handleTFASuccess}
        onBack={handleTFABack}
        username={fullPhoneNumber}
        password={formData.password || ""}
        onLoginSuccess={() => navigate("/dashboard")}
      />
    );
  }

  // Show Admin Password Reset form
  if (showAdminPasswordReset && adminPasswordResetData) {
    return (
      <AdminPasswordResetForm
        userId={adminPasswordResetData.userId}
        passwordResetToken={adminPasswordResetData.passwordResetToken}
      />
    );
  }

  // Show Forgot Password form
  if (showForgotPassword) {
    return (
      <ForgotPasswordForm
        onBack={handleForgotPasswordBack}
        onSuccess={handleForgotPasswordSuccess}
      />
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Sol Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#9B51E0] relative overflow-hidden">
        {/* Dekoratif Şekiller */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-32 left-20 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/5 rounded-full"></div>
        </div>

        {/* İçerik */}
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">EduPayz</h1>
            <p className="text-xl text-white/80 mb-2">Eğitim Yönetim Sistemi</p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg">Kolay Ödeme Takibi</h3>
                <p className="text-white/70 text-sm">
                  Tüm ödemeleri tek bir yerden yönetin
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg">Öğrenci Yönetimi</h3>
                <p className="text-white/70 text-sm">
                  Öğrenci ve veli bilgilerini kolayca takip edin
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-lg">Detaylı Raporlama</h3>
                <p className="text-white/70 text-sm">
                  Finansal raporları anında oluşturun
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sağ Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobil Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo.png"
              alt="EduPayz Logo"
              className="h-16 w-auto object-contain mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">EduPayz</h1>
          </div>

          <div className="mb-4 flex flex-col items-center">
            <div className="mb-6 self-center">
              <img
                src="/logo.png"
                alt="EduPayz Logo"
                className="h-20 w-auto object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Hoş Geldiniz</h2>
            <p className="text-gray-500 mt-2">
              Hesabınıza giriş yaparak devam edin
            </p>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Country Code and Phone Number */}
                <div className="space-y-2">
                  <Label
                    htmlFor="phoneNumber"
                    className="text-sm font-medium text-gray-700"
                  >
                    Telefon Numarası
                  </Label>
                  <div className="flex space-x-2">
                    <SimpleDropdown
                      options={countryCodes.map((code) => ({
                        id: code.code,
                        label: `${code.flag} +${code.code}`,
                        value: code.code,
                      }))}
                      value={selectedCountryCode}
                      onChange={(countryCode) => {
                        if (countryCode) {
                          setSelectedCountryCode(countryCode);
                          setCountryCode(String(countryCode.value));
                          setFormData((prev) => ({ ...prev, username: "" }));
                          setErrors((prev) => ({ ...prev, username: "" }));
                        }
                      }}
                      placeholder="Ülke"
                      className="w-28"
                    />
                    <IMaskInput
                      id="phoneNumber"
                      name="username"
                      type="tel"
                      mask="(000) 000-0000"
                      placeholder="(5XX) XXX-XXXX"
                      value={formData.username || ""}
                      onAccept={(value: string) =>
                        setFormData((prev) => ({ ...prev, username: value }))
                      }
                      className="flex-1 text-left text-sm font-mono px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B51E0]/20 focus:border-[#9B51E0] transition-all hover:border-gray-300 bg-white"
                      inputMode="numeric"
                    />
                  </div>
                  {errors.username && (
                    <p className="text-sm text-red-500 flex items-center mt-1">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.username}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-sm font-medium text-gray-700"
                  >
                    Şifre
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      placeholder="••••••"
                      className="pr-12 h-11 border-gray-200 focus:ring-2 focus:ring-[#9B51E0]/20 focus:border-[#9B51E0]"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500 flex items-center mt-1">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Remember Me and Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="rememberMe"
                      checked={rememberMe}
                      onCheckedChange={(checked) =>
                        setRememberMe(checked === true)
                      }
                      className="border-gray-300 data-[state=checked]:bg-[#9B51E0] data-[state=checked]:border-[#9B51E0]"
                    />
                    <Label
                      htmlFor="rememberMe"
                      className="text-sm text-gray-600 cursor-pointer"
                    >
                      Beni hatırla
                    </Label>
                  </div>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-[#9B51E0] hover:text-[#8B41D0] font-medium transition-colors"
                  >
                    Şifremi unuttum
                  </button>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#9B51E0] hover:bg-[#8B41D0] text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-[#9B51E0]/25 hover:shadow-xl hover:shadow-[#9B51E0]/30"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Giriş yapılıyor...
                    </span>
                  ) : (
                    "Giriş Yap"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 EduPayz. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
